'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import { ArrowRight, Clock, Calendar } from 'lucide-react';
import { useSiteData } from '@/lib/dataCache';
import OptimizedImage from '@/components/ui/OptimizedImage';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

const GRADIENTS = [
  'linear-gradient(135deg, #6C63FF 0%, #8D84FF 100%)',
  'linear-gradient(135deg, #FF6584 0%, #FF84A2 100%)',
  'linear-gradient(135deg, #5A50F0 0%, #FF6584 100%)',
];

const EMOJIS = ['✨', '⚡', '🚀', '🔷', '🛠️', '💡'];

type ApiBlogPost = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  coverImage?: string | null;
  category: string;
  tags: string[];
  readTime: number;
  publishedAt?: string | null;
  createdAt: string;
};

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function Blog() {
  const posts = useSiteData<ApiBlogPost[]>('blog', '/api/blog', []);

  const latest = posts.slice(0, 3);

  return (
    <section id="blog" className="blog-section">
      <div className="section-container">
        <motion.div
          className="section-heading"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
        >
          <span className="section-label">Latest Articles</span>
          <h2 className="section-title">From The Blog</h2>
          <p className="section-subtitle">
            Thoughts, tutorials, and insights on modern web development
          </p>
        </motion.div>

        <div className="blog-grid">
          {latest.length === 0 && (
            <div className="blog-empty">No articles published yet.</div>
          )}
          {latest.map((post, i) => {
            const gradient = GRADIENTS[i % GRADIENTS.length];
            const emoji = EMOJIS[i % EMOJIS.length];
            const dateStr = formatDate(post.publishedAt || post.createdAt);
            return (
              <motion.article
                key={post.id}
                className="blog-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
              >
                <div className="blog-card-header" style={{ background: gradient }}>
                  {post.coverImage ? (
                    <OptimizedImage
                      src={post.coverImage}
                      alt={post.title}
                      className="blog-card-img"
                      style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                      loading="lazy"
                    />
                  ) : (
                    <span className="blog-card-emoji">{emoji}</span>
                  )}
                  <span className="blog-card-tag">{post.category}</span>
                </div>

                <div className="blog-card-body">
                  <h3 className="blog-card-title">{post.title}</h3>
                  <p className="blog-card-excerpt">{post.excerpt}</p>

                  <div className="blog-card-meta">
                    {dateStr && (
                      <>
                        <span className="blog-card-meta-item">
                          <Calendar size={14} />
                          {dateStr}
                        </span>
                        <span className="blog-card-meta-dot" />
                      </>
                    )}
                    <span className="blog-card-meta-item">
                      <Clock size={14} />
                      {post.readTime} min read
                    </span>
                  </div>

                  <Link href={`/blog/${post.slug}`} className="blog-card-link">
                    Read More
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.article>
            );
          })}
        </div>

        <motion.div
          className="blog-cta"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Link href="/blog" className="btn btn-outline">
            View All Articles
            <ArrowRight size={18} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
